import type { BeforeSendFn, CaptureResult } from 'posthog-js'

/**
 * Bounds what exception autocapture is allowed to send. Three guards, in
 * order: a deny list of known-benign messages, a per-session fingerprint so a
 * crash loop collapses to one event, and a hard cap so a pathological session
 * cannot spend the whole event budget.
 *
 * This is the ONLY filter point. It runs inside `before_send`, which sees
 * autocaptured and explicit `captureException` calls alike. Do not also call
 * `shouldCaptureException` from a capture wrapper: it mutates the dedup set,
 * so a second call for the same error would drop it as a duplicate.
 *
 * Plan: docs/superpowers/plans/2026-09-09-posthog-observability-plan.md, task 1.
 */

/** Per-session ceiling. A crash loop must not spend the whole event budget. */
export const MAX_EXCEPTIONS_PER_SESSION = 25

/**
 * Ported from the retired edge-frontend Sentry `ignoreErrors` list
 * (edge-frontend/src/index.jsx:30-52), minus the entries tied to stacks we
 * no longer ship. Every entry is a known-benign or known-unactionable
 * condition. Keep each pattern specific: a loose match here silently hides
 * real bugs.
 */
const IGNORED: RegExp[] = [
  // Benign browser quirk; fires when a resize callback schedules more layout
  // than fits in one frame. Also filtered in main.tsx's dev crash log.
  /^ResizeObserver loop/,
  // Audio autoplay refused by the user agent. Not actionable client-side.
  /play\(\) failed|play method is not allowed/,
  // Network layer: aborted, offline, or blocked. Reported elsewhere as
  // workspace connection events, not as crashes.
  /^(AxiosError: )?(Request aborted|Network Error)$|^Failed to fetch$|^Load failed$/,
  // Auth race on logout; the guard redirects and the error is moot.
  /^No Authenticated User/,
  // A thrown non-Error (string, object). No stack, no grouping, no value.
  /^Non-Error (exception|promise rejection) captured/,
  // Stale-deploy chunk misses are self-healing via the boundary's reload
  // prompt. The boundary reports them as a product event instead, so they
  // stay visible without eating the exception budget.
  /^Failed to fetch dynamically imported module|^Loading (CSS )?chunk|^ChunkLoadError/,
]

let captured = 0
const seen = new Set<string>()

/** The frame the error was thrown from. posthog-js lists frames outermost-first. */
type Frame = { filename?: string; lineno?: number; colno?: number }

/**
 * Type + message + throw-site location. Collapses a repeating error to one
 * event while keeping the same message thrown from two places distinct.
 */
export function fingerprint(type: string, value: string, frame: Frame | undefined): string {
  const site = frame ? `${frame.filename ?? ''}:${frame.lineno ?? ''}:${frame.colno ?? ''}` : ''
  return `${type}:${value}::${site}`
}

export function shouldCaptureException(type: string, value: string, frame?: Frame): boolean {
  if (IGNORED.some((re) => re.test(value))) return false
  if (captured >= MAX_EXCEPTIONS_PER_SESSION) return false
  const fp = fingerprint(type, value, frame)
  if (seen.has(fp)) return false
  seen.add(fp)
  captured += 1
  return true
}

/** The slice of `$exception_list` this filter reads. Mirrors @posthog/core's Exception. */
interface ExceptionEntry {
  type?: string
  value?: string
  stacktrace?: { frames?: Frame[] }
}

/**
 * `before_send` hook. Only inspects `$exception` events; everything else
 * passes through untouched. Returning null drops the event.
 */
export const exceptionBeforeSend: BeforeSendFn = (cr: CaptureResult | null) => {
  if (!cr || cr.event !== '$exception') return cr
  const list = cr.properties?.$exception_list as ExceptionEntry[] | undefined
  const first = list?.[0]
  // No structured exception to judge: let it through rather than guess.
  if (!first) return cr
  const frames = first.stacktrace?.frames
  const throwSite = frames?.[frames.length - 1]
  return shouldCaptureException(first.type ?? 'Error', first.value ?? '', throwSite) ? cr : null
}

/** Test-only: reset the per-session counters. */
export function __resetExceptionFilterForTests(): void {
  captured = 0
  seen.clear()
}
